import ColumnBase from "./ColumnBase.jsx";
import { ColumnStore } from "@bryntum/schedulerpro";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import { ICON_COL_DEF_WIDTH } from "../../other/constants/OtherKeys.jsx";
import { defaultGroupRenderer, openGeneralWidgetFromGridRecord } from "../../other/utils/utils.jsx";
import { WIDGET_REGISTRY } from "../../other/constants/EntityManagerConfig.tsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import { HasIconColumnTooltipMixin } from "./mixins/HasIconColumnTooltipMixin.jsx";

const getNotesIcon = (hasNotes = false) => {
  const notesColor = hasNotes ? "text-[#0a88e9]" : "";
  return `<i class="swivu-notes-icon-tooltip fa-regular fa-note-sticky ${notesColor}"></i>`;
};

export default class NotesColumn extends HasIconColumnTooltipMixin(ColumnBase) {
  static get $name() {
    return "NotesColumn";
  }

  static get type() {
    return WIDGET_KEYS.notesColumn;
  }

  static get defaults() {
    return {
      text: "Notes",
      width: 30,
      minWidth: ICON_COL_DEF_WIDTH,
      editor: false,
      searchable: false,
      align: "center",
      field: "hasNotes",
      cellCls: "cursor-pointer",
      cls: "swivu-icon-column",
      sealed: true,
      filterable: false,
      htmlEncode: false,
      renderer({ value }) {
        return getNotesIcon(value);
      },
      groupRenderer({ groupRowFor, ...rest }) {
        return defaultGroupRenderer({ groupRowFor: getNotesIcon(groupRowFor), ...rest });
      },
      headerRenderer() {
        return getNotesIcon();
      }
    };
  }

  /**
   * Get the CSS selector for the tooltip
   * @returns {String} - The CSS selector
   */
  getTooltipSelector() {
    return ".swivu-notes-icon-tooltip";
  }

  /**
   * Get the notes to display in the tooltip
   * @param {Object} record - The record to get notes for
   * @returns {Array} - The notes to display
   */
  getTooltipItems(record) {
    return this.project.boardNotesStore.query(note => {
      if (record.isTask || record.isPlanTaskModel) return note.taskId === record.id;
      if (record.isResource) return note.resourceId === record.id;
      if (record.isClient) return note.clientId === record.id;
      return false;
    });
  }

  /**
   * Format the notes for display in the tooltip
   * @param {Array} notes - The notes to format
   * @returns {Array} - The formatted notes
   */
  formatTooltipItems(notes) {
    return notes.map((noteRecord, index) => {
      // Limit description to a reasonable length
      const description = noteRecord.name || "";
      const truncatedDescription = description.length > 50 ?
        description.substring(0, 50) + "..." : description;

      return {
        tag: "div",
        dataset: {
          noteId: noteRecord.id
        },
        class: `tooltip-contact-container flex flex-col gap-2 ${index > 0 ? 'pt-3 mt-3 border-t border-border' : ''}`,
        children: [{
          tag: "div",
          class: "contact-pill contact-header font-medium text-sm cursor-pointer hover:bg-accent/80 transition-colors rounded-md px-3 py-2",
          html: `
            <div class="flex flex-col gap-1">
              <div class="text-sm font-medium text-foreground">${truncatedDescription || "Untitled Note"}</div>
              <div class="text-xs text-muted-foreground">Created By: ${noteRecord.creatorName || 'N/A'}</div>
            </div>
          `
        }]
      };
    });
  }

  /**
   * Handle clicks on notes in the tooltip
   * @param {Event} event - The click event
   * @param {Object} record - The record the tooltip is for
   */
  handleTooltipClick(event, record) {
    if (this.onNoteTimeout) clearTimeout(this.onNoteTimeout);

    this.onNoteTimeout = setTimeout(() => {
      // Find the container using .closest() for better nested element handling
      const tooltipNoteContainerEl = event.target.closest(".tooltip-contact-container");
      if (!tooltipNoteContainerEl) return;

      // Check if clicked on metadata (don't open editor)
      if (event.target.closest(".text-muted-foreground")) return;

      const noteRecord = this.project.boardNotesStore.getById(tooltipNoteContainerEl.dataset.noteId);
      if (!noteRecord) return;

      // Open the note editor
      this.otherParams.editorHandler.openEditor(
        EDITOR_KEYS.noteEditor,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        null,
        {
          entityRecord: record,
          noteRecord,
        },
        { by: "recordType", useExisting: true }
      );
    }, 100);
  }

  onSwivuColumnClick(e) {
    openGeneralWidgetFromGridRecord(this.editorHandler, e.record, WIDGET_REGISTRY.notes.id);
  }
}

ColumnStore.registerColumnType(NotesColumn);
